import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/common/SEO';
import HeroSection from '../components/common/HeroSection';
import Button from '../components/common/Button';

const programDetails = [
  {
    label: 'Cost',
    value: 'Free'
  },
  {
    label: 'Group Size',
    value: '8–20 participants'
  },
  {
    label: 'Language',
    value: 'English'
  },
  {
    label: 'Program Duration',
    value: 'Cycles repeat continuously'
  },
  {
    label: 'Frequency',
    value: 'Every Tuesday, starting August 25, 2026'
  },
  {
    label: 'Session Length',
    value: '75 minutes per session'
  }
];

const programBenefits = [
  'Name and normalize your migration stress experience without self-blame or shame',
  'Recognize and respond to your own emotional and physical signals of overload before reaching a crisis point',
  'Identify the invisible mental load you carry and develop realistic strategies for managing it',
  'Navigate relationships and communication with family, community, and everyday U.S. systems with greater confidence and clearer boundaries',
  'Build or strengthen at least one genuine connection or community anchor in your local environment',
  'Reconnect with your core identity and values independent of roles, geography, or cultural expectation',
  'Envision a life beyond survival mode, articulating what a fulfilling, purposeful life in the U.S. looks like for you personally',
  'Build a sustainable personal wellness rhythm and know when and how to seek professional support without shame'
];

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.55 }
};

const primaryButtonStyle =
  '!border-[#81258A] !bg-[#81258A] !text-white hover:!border-[#6d1f75] hover:!bg-[#6d1f75]';

const ResilientHorizons = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white">
      <SEO
        title="Resilient Horizons"
        description="Resilient Horizons is a free coaching and connection circle supporting migrant and immigrant women in the United States."
        image="/assets/images/resilient-horizons.png"
      />

      {/* Keep the same header and hero format used on Education Services */}
      <HeroSection
        title="Resilient Horizons"
        variant="services"
        height="h-[320px]"
      />

      {/* What this group provides */}
      <section className="bg-white py-14 md:py-20">
        <div className="container mx-auto px-4">
          <div className="grid gap-12 lg:grid-cols-[1.25fr_0.75fr] lg:items-start">
            <motion.div {...fadeUp}>
              <p className="text-sm font-medium uppercase text-gray-700">
                A coaching & connection circle for migrant women
              </p>

              <h2 className="mt-5 text-2xl font-bold uppercase text-gray-900">
                What This Group Provides
              </h2>

              <div className="mt-8 space-y-5 text-base leading-7 text-gray-700">
                <p>
                  Migration is one of the most psychologically complex
                  experiences someone can go through, and it is almost never
                  acknowledged that way. Between speaking a new language,
                  feeling pressure to appear strong, holding together a
                  household and dealing with grief from leaving family behind,
                  migrant women are dealing with a lot.
                </p>

                <p>
                  The mental health consequences are real, well-documented,
                  and common across migrant women regardless of background and
                  circumstances. Most resources are either clinical, requiring
                  a diagnosis or referral, English-language-barrier-dependent,
                  culturally mismatched, or simply inaccessible due to cost,
                  childcare, or scheduling constraints.
                </p>

                <p>
                  Resilient Horizons exists to fill that gap—providing
                  structured, culturally informed, psycho-education-based
                  coaching in a peer community format. It is free, flexible,
                  and designed from the ground up for you.
                </p>

                <p>
                  This group is for migrant and immigrant women living in the
                  United States from all cultural backgrounds, and Asian
                  migrant women in particular. All are welcome regardless of
                  employment status, education level, immigration status, or
                  length of time in the U.S.
                </p>

                <p>
                  The program runs as an 8-week cycle on a fixed day and time
                  each week via Zoom. It is open-door, so anyone can join at
                  any point in the cycle without feeling lost. Each session
                  stands alone while contributing to a larger journey.
                </p>
              </div>
            </motion.div>

            <motion.div
              className="mx-auto w-full max-w-sm"
              {...fadeUp}
              transition={{ duration: 0.55, delay: 0.08 }}
            >
              <img
                src="/assets/images/resilient-horizons.png"
                alt="Resilient Horizons program"
                className="w-full rounded-[18px] object-cover"
              />
            </motion.div>
          </div>

          {/* Program information boxes */}
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {programDetails.map((detail, index) => (
              <motion.div
                key={detail.label}
                className="rounded-[18px] bg-[#f1e2ff] px-6 py-7 text-center"
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <p className="text-sm font-medium uppercase text-gray-900">
                  {detail.label}
                </p>

                <p className="mt-1 text-base text-gray-800">
                  {detail.value}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Community and benefits */}
      <section className="bg-[#f1e2ff] py-14 md:py-20">
        <div className="container mx-auto px-4">
          <p className="text-sm font-medium uppercase text-gray-700">
            Envision a calmer, stronger you
          </p>

          <h2 className="mt-5 text-2xl font-bold uppercase text-gray-900">
            A Community to Listen, Learn, Lean On
          </h2>

          <div className="mt-8 grid gap-12 lg:grid-cols-[1.35fr_0.65fr] lg:items-start">
            <motion.div {...fadeUp}>
              <div className="space-y-5 text-base leading-7 text-gray-700">
                <p>
                  Resilient Horizons is a structured, facilitated space where
                  migrant women come together to be heard, think out loud,
                  learn practical tools for emotional well-being, and remind
                  each other that they are more capable than their hardest days
                  make them feel.
                </p>

                <p>
                  By the end of the 8-week cycle, you will be able to:
                </p>
              </div>

              <ul className="mt-5 space-y-3">
                {programBenefits.map((benefit) => (
                  <li
                    key={benefit}
                    className="flex items-start gap-3 text-base leading-7 text-gray-700"
                  >
                    <span className="mt-[10px] h-2 w-2 flex-none rounded-full bg-[#81258A]" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-8 text-sm leading-6 text-gray-700">
                Please note: This program is not for those in acute mental
                health crisis or needing clinical intervention, although we can
                help you find professional support if needed.
              </p>
            </motion.div>

            <motion.div
              className="mx-auto w-full max-w-md"
              {...fadeUp}
              transition={{ duration: 0.55, delay: 0.08 }}
            >
              <img
                src="/assets/images/resilient-community.png"
                alt="A supportive community of women"
                className="aspect-[4/3] w-full rounded-[18px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Registration call to action */}
      <section className="bg-white py-16 md:py-20">
        <motion.div
          className="container mx-auto px-4 text-center"
          {...fadeUp}
        >
          <p className="text-sm font-medium uppercase text-gray-700">
            Register today
          </p>

          <h2 className="mt-4 text-2xl font-bold uppercase text-gray-900">
            Fill Out the Form to Join
          </h2>

          <div className="mt-8">
            <a href="https://forms.cloud.microsoft/r/3vMJ1vFVSw">
              <Button
                className={`justify-center ${primaryButtonStyle}`}
              >
                Register Now
              </Button>
            </a>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default ResilientHorizons;